import { useMemo, useCallback } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { dateIsAfter, dateIsBefore, isSameDay } from 'lib/datetime/dateUtils'
import { getCalendarMonthWeeks } from 'lib/datetime/time'
import { rem } from 'polished'
import moment from 'moment'
import clsx from 'clsx'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import Clickable from '../Clickable'
import BodyText from '../../../../LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import CalendarEventOverlayIcon from './CalendarEventOverlayIcon'
import { useCalendarEventTooltip } from './CalendarEventTooltipBubble'
import {
  CALENDAR_EVENT_OVERLAY_FILL,
  getCalendarEventOverlayForIsoDate,
  getCalendarPublicHolidayForIsoDate,
} from './calendarEventOverlay'

const Grid = styled.div`
  display: grid;
  position: relative;
  grid-template-columns: repeat(7 , 1fr);
  grid-auto-rows: ${rem(44)};
`

const DayCell = styled(Clickable)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-inline-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  isolation: isolate;
  transition: background-color 0.2s;

  &.row-0 {
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.column-0 {
    border-inline-start: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &:disabled {
    color: ${props => props.theme.palette.neutral.surface.lightForeground};

    background-image: linear-gradient(
      to top right,
      white 50%,
      ${props => props.theme.palette.neutral.surface.lightForeground},
      white 52%
    );
  }

  &:not(:disabled):not(.selected-date) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      }
    }
    &:focus {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    }
  }

  &.selected-date {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.in-range-date {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.event-overlay:not(.selected-date):not(.in-range-date):not(:disabled)::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    border-radius: inherit;
    background-color: ${CALENDAR_EVENT_OVERLAY_FILL};
  }

  &.event-overlay-start:not(.selected-date):not(.in-range-date):not(:disabled) {
    border-start-start-radius: ${props => props.theme.borderRadius.S};
    border-end-start-radius: ${props => props.theme.borderRadius.S};
  }

  &.event-overlay-end:not(.selected-date):not(.in-range-date):not(:disabled) {
    border-start-end-radius: ${props => props.theme.borderRadius.S};
    border-end-end-radius: ${props => props.theme.borderRadius.S};
  }
`

const OverlayIconSlot = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(50% - ${rem(10)});
  line-height: 0;
  pointer-events: none;

  &.start {
    inset-inline-start: 0;
  }

  &.end {
    inset-inline-end: 0;
  }
`

const HolidayPip = styled.span`
  position: absolute;
  z-index: 1;
  bottom: ${rem(4)};
  inset-inline-start: 50%;
  width: ${rem(6)};
  height: ${rem(6)};
  border-radius: 50%;
  background-color: ${props => props.theme.palette.neutral.default.one};
  transform: translateX(-50%);
  pointer-events: auto;
`

/**
 * Connects consecutive holiday days into one period.
 * The bar grows from the centre of the first and last tiles, where the endpoint
 * dots sit, and runs edge-to-edge through the days in between.
 */
const HolidayBar = styled.span`
  position: absolute;
  bottom: ${rem(6)};
  height: ${rem(2)};
  background-color: ${props => props.theme.palette.neutral.default.one};
  pointer-events: auto;

  &:not(.start) {
    inset-inline-start: 0;
  }

  &.start {
    inset-inline-start: 50%;
  }

  &:not(.end) {
    inset-inline-end: 0;
  }

  &.end {
    inset-inline-end: 50%;
  }
`

const HiddenHolidayName = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`

interface PublicHolidayMarkerProps {
  holidayId: string
  name: string
  isRange: boolean
  isStart: boolean
  isEnd: boolean
}

function PublicHolidayMarker(props: PublicHolidayMarkerProps) {
  const { holidayId, name, isRange, isStart, isEnd } = props
  const { bubble, hide: hideTooltip, show: showTooltip } = useCalendarEventTooltip(
    name,
    `calendar-public-holiday-tooltip-${holidayId}`,
  )

  const onMouseEnter = useCallback((mouseEvent: React.MouseEvent<HTMLSpanElement>) => {
    showTooltip(mouseEvent.currentTarget)
  }, [showTooltip])

  const showPip = !isRange || isStart || isEnd

  return (
    <>
      {isRange && (
        <HolidayBar
          className={clsx({ start: isStart, end: isEnd })}
          data-testid={`calendar-public-holiday-bar-${holidayId}`}
          onMouseEnter={onMouseEnter}
          onMouseLeave={hideTooltip}
        />
      )}
      {showPip && (
        <HolidayPip
          data-testid={`calendar-public-holiday-${holidayId}`}
          onMouseEnter={onMouseEnter}
          onMouseLeave={hideTooltip}
        />
      )}
      <HiddenHolidayName>{name}</HiddenHolidayName>
      {bubble}
    </>
  )
}

const EmptyCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  &.top-row {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.last-empty {
    border-inline-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const SelectedDateLabel = styled.span`
  position: absolute;
  top: 0;
  font-size: ${rem(10)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  display: none;

  ${mediaQueryUp.desktop} {
    display: inline;
  }
`

interface Props {
  startOfMonth: Date
  minDate?: Date
  maxDate?: Date
  onDayClick: (day: moment.Moment) => void
  startDate?: Date
  endDate?: Date
  startLabel?: string
  endLabel?: string
  overlayEvents?: ReadonlyArray<Pick<SearchCalendarEvent, 'id' | 'startDate' | 'endDate' | 'color' | 'icon'>>
  overlayHolidays?: ReadonlyArray<Pick<SearchCalendarEvent, 'id' | 'startDate' | 'endDate' | 'title'>>
}

function holidaySegmentFor(
  holiday: ReturnType<typeof getCalendarPublicHolidayForIsoDate>,
): 'day' | 'start' | 'middle' | 'end' | undefined {
  if (!holiday) return undefined
  if (!holiday.isRange) return 'day'
  if (holiday.isStart) return 'start'
  if (holiday.isEnd) return 'end'
  return 'middle'
}

function CalendarGrid(props: Props) {
  const {
    startOfMonth,
    minDate,
    maxDate,
    onDayClick,
    startDate,
    endDate,
    startLabel,
    endLabel,
    overlayEvents,
    overlayHolidays,
  } = props

  const monthIndex = startOfMonth.getMonth()
  const weeks = useMemo(() => getCalendarMonthWeeks(moment(startOfMonth), true), [startOfMonth])

  const onClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.currentTarget.dataset?.date) {
      const day = moment(+e.currentTarget.dataset.date)
      onDayClick(day)
    }
  }, [onDayClick])

  return (
    <Grid>
      {weeks.map((week, weekIndex) => week.map((day, dayIndex) => {
        if (!day) {
          return <EmptyCell
            key={`${monthIndex}-${weekIndex}-${dayIndex}`}
            className={clsx({ 'top-row': weekIndex === 0, 'last-empty': !!week[dayIndex + 1] })}
          />
        }

        const dateOfDay = day.toDate()

        let disabled: boolean = false
        if (minDate && maxDate) {
          disabled = dateIsBefore(dateOfDay, minDate) || dateIsAfter(dateOfDay, maxDate)
        } else if (minDate || maxDate) {
          disabled = dateIsBefore(dateOfDay, minDate) || dateIsAfter(dateOfDay, maxDate)
        }

        const isStartDate = (startDate && isSameDay(dateOfDay, startDate))
        const isEndDate = !isStartDate && (endDate && isSameDay(dateOfDay, endDate))
        const isSelected = isStartDate || isEndDate
        const hasLabel = (isStartDate && startLabel) || (isEndDate && endLabel)
        const isInRange = (!isSelected && startDate && endDate) ? dateIsAfter(dateOfDay, startDate) && dateIsBefore(dateOfDay, endDate) : false
        const isoDate = day.format(ISO_DATE_FORMAT)
        const overlay = overlayEvents?.length
          ? getCalendarEventOverlayForIsoDate(overlayEvents, isoDate)
          : undefined
        const holiday = overlayHolidays?.length
          ? getCalendarPublicHolidayForIsoDate(overlayHolidays, isoDate)
          : undefined
        const showEventChrome = !!overlay && !disabled && !isSelected && !isInRange
        const showEventIcon = showEventChrome && (overlay.isStart || overlay.isEnd)
        const holidaySegment = holidaySegmentFor(holiday)

        return <DayCell
          key={`${monthIndex}-${weekIndex}-${dayIndex}`}
          disabled={disabled}
          className={clsx(`row-${weekIndex}`, `column-${dayIndex}`, {
            'selected-date': isSelected,
            'in-range-date': isInRange,
            'event-overlay': showEventChrome,
            'event-overlay-start': showEventChrome && overlay.isStart,
            'event-overlay-end': showEventChrome && overlay.isEnd,
            'public-holiday-range': holiday?.isRange,
            'public-holiday-range-start': holiday?.isRange && holiday.isStart,
            'public-holiday-range-end': holiday?.isRange && holiday.isEnd,
          })}
          style={showEventChrome ? { '--event-overlay-color': overlay.color } as React.CSSProperties : undefined}
          data-testid={`day-${isoDate}`}
          data-event-overlay={showEventChrome ? overlay.id : undefined}
          data-public-holiday={holiday?.id}
          data-public-holiday-segment={holidaySegment}
          data-date={day.valueOf()}
          onClick={onClick}
        >
          {(isSelected && hasLabel) && <SelectedDateLabel>{isStartDate ? startLabel : endLabel}</SelectedDateLabel>}
          <BodyText variant="medium">{day.date()}</BodyText>
          {showEventIcon && overlay.isStart && (
            <OverlayIconSlot className="start" data-testid="calendar-event-overlay-icon-start">
              <CalendarEventOverlayIcon name={overlay.icon} size={12} />
            </OverlayIconSlot>
          )}
          {showEventIcon && overlay.isEnd && (
            <OverlayIconSlot className="end" data-testid="calendar-event-overlay-icon-end">
              <CalendarEventOverlayIcon name={overlay.icon} size={12} />
            </OverlayIconSlot>
          )}
          {!!holiday && !disabled && (
            <PublicHolidayMarker
              holidayId={holiday.id}
              name={holiday.title}
              isRange={holiday.isRange}
              isStart={holiday.isStart}
              isEnd={holiday.isEnd}
            />
          )}
        </DayCell>
      }))}
    </Grid>
  )
}

export default React.memo(CalendarGrid)
